<template>
  <section class="product-specs">
    <close-button class="close-button" @click="$emit('close')"></close-button>
    <div class="dialog-header">
      <h1>{{ title || 'Print specificaties' }}</h1>
    </div>
    <div class="dialog-content">
      <product
        v-if="products.length === 1"
        :product="product"
        class="product"
        :show-select-box="false"
      >
      </product>
      <section class="products" v-else-if="products.length > 1">
        <span
          id="trigger"
          @click="showList"
          slot="trigger"
          class="products-trigger"
          ><button
            class="chevron right"
            :class="{ 'point-down': showProductList }"
          ></button
          >{{ products.length }} ontwerpen</span
        >
        <transition name="fade">
          <product-list-simple
            v-show="showProductList"
            class="product-list"
            :products="products"
            v-click-outside:trigger="hideList"
          ></product-list-simple>
        </transition>
      </section>
      <div class="specs">
        <counter
          title="Aantal"
          :count="products[0].quantity"
          @set-count="setQuantity"
        ></counter>

        <div class="menu" v-for="option in specs" :key="option.title">
          <div class="single-spec" v-if="option.values.length === 1">
            <span class="title">{{ option.title }}</span
            ><span class="spec">{{ option.values[0] }}</span>
          </div>

          <slider
            v-else-if="option.menuType === 'slider'"
            :options="option"
            @set-val="setVal"
            :value="productSpecs[option.title]"
          ></slider>
          <colors-menu
            v-else-if="option.menuType === 'colors'"
            :options="option"
            @set-color="setColor"
            :active="productSpecs[option.title]"
          ></colors-menu>
          <option-menu
            v-else-if="option.menuType === 'option'"
            :options="option"
            @set-val="setVal"
            :active="productSpecs[option.title]"
          ></option-menu>
        </div>
      </div>
      <div class="button-holder">
        <button
          class="pill-button"
          type="submit"
          :disabled="quantity < 1"
          @click="editProduct"
        >
          {{ buttonLabel || 'toevoegen aan pakket' }}
        </button>
      </div>
    </div>
  </section>
</template>

<script>
import Counter from '@/components/ui/Counter.vue'
import OptionMenu from '@/components/ui/OptionMenu.vue'
import ColorsMenu from '@/components/ui/ColorsMenu.vue'

import Slider from '@/components/ui/Slider.vue'
import Product from './ProductSelector/Product.vue'
import ProductListSimple from './ProductListSimple.vue'
import { mapStores } from 'pinia'
import { usePrintStoreStore } from '@/features/printstore/PrintStore.store'

export default {
  props: {
    products: {
      default: () => {
        return []
      },
      type: Array
    },
    title: {},
    buttonLabel: {},
    callback: {},
    productIndex: {}
  },
  components: {
    Product,
    Counter,
    Slider,
    OptionMenu,
    ProductListSimple,
    ColorsMenu
  },
  data() {
    return {
      quantity: 1,
      productType: {},
      productSpecs: {},
      multiSpecs: false,
      product: {},
      showProductList: false,
      variant: {}
    }
  },
  mounted() {
    if (this.products.length > 1) {
      this.multiSpecs = true
    }
    this.product = { ...this.products[0] }
    this.quantity = this.product.quantity || 1

    this.productType = this.printStoreStore.productTypes.find(
      (p) => p.id === this.product.productTypeId
    )

    this.$set(
      this.productSpecs,
      this.productType.printSpecification.products.title,
      this.product.printSpecification[
        this.productType.printSpecification.products.title
      ]
    )

    if (this.productType.printSpecification.colors) {
      this.$set(
        this.productSpecs,
        this.productType.printSpecification.colors.title,
        this.product.printSpecification[
          this.productType.printSpecification.colors.title
        ]
      )
    }

    this.productType.printSpecification.specs.forEach((spec) => {
      this.$set(
        this.productSpecs,
        spec.title,
        this.product.printSpecification[spec.title]
      )
    })

    if (this.product.hasVariants) {
      this.variant = this.product.variant
    }
  },
  computed: {
    ...mapStores(usePrintStoreStore),
    specs() {
      if (this.products.length < 1 || !this.printStoreStore.productTypes) {
        return []
      }

      const productType = this.printStoreStore.productTypes.find(
        (p) => p.id === this.product.productTypeId
      )

      if (!productType?.printSpecification) return []

      let specsMenus = []

      specsMenus[0] = {
        title: productType.printSpecification.products.title,
        menuType: productType.printSpecification.products.menuType,
        property: 'size',
        values: productType.printSpecification.products.values
      }

      if (productType.printSpecification.colors) {
        specsMenus.push({
          title: productType.printSpecification.colors.title,
          menuType: productType.printSpecification.colors.menuType,
          property: 'color',
          values: productType.printSpecification.colors.values
        })
      }

      if (productType.printSpecification.specs?.[0]) {
        specsMenus.push({
          title: productType.printSpecification.specs[0].title,
          menuType: productType.printSpecification.specs[0].menuType,
          property: 'application',
          values: productType.printSpecification.specs[0].values
        })
      }

      return specsMenus
    }
  },
  methods: {
    setQuantity(val) {
      this.quantity = val
    },

    setVal(title, val, property, value) {
      this.productSpecs[title] = val
      this.variant[property] = value
    },
    setColor(title, name, prop, pname) {
      this.productSpecs[title] = name
      this.variant.color = pname
      this.product.previewImage = this.product.previews.find(
        (img) => img.colour === pname
      ).uuid

      this.variant.previewImage = this.product.previewImage
    },
    editProduct() {
      if (this.products.length === 1) {
        let newProduct = {
          ...this.product
        }

        newProduct.quantity = this.quantity
        newProduct.printSpecification = this.productSpecs
        newProduct.printSpecification.title = this.product.printSpecification.title

        if (this.product.hasVariants) {
          newProduct.variant = this.variant
          // newProduct.variant.previewImage = this.product.previewImage
        }

        this.callback(newProduct, this.productIndex)
      } else {
        this.callback([this.quantity, this.productSpecs, this.variant])
      }

      this.$emit('close')
    },
    showList() {
      this.showProductList = !this.showProductList
    },
    hideList() {
      this.showProductList = false
    }
  }
}
</script>

<style lang="scss" scoped>
@use '../../../sass/base/_colors.scss' as *;

.product {
  background-color: transparent;
  box-shadow: none;

  &:hover {
    box-shadow: none;
    cursor: default;
  }
}

.menu {
  padding: 10px 0;
}

.specs {
  margin-bottom: 20px;
}

.products-trigger {
  color: $text-color;
  cursor: pointer;

  .chevron {
    color: var(--sub-color);
    margin-right: 5px;
  }
}

.point-down {
  transform: rotate(90deg);
}

.products {
  position: relative;
  padding-bottom: 10px;
}

.product-list {
  position: absolute;
  z-index: 10;
  width: 100%;
}

.single-spec {
  line-height: 15px;

  .spec {
    font-weight: 500;
    margin-left: 6px;
  }
}
</style>
